import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI, authAPI } from '../api/api';
import UserForm from '../components/Users/UserForm';
import { formatDateForDisplay } from '../utils/dateUtils';
import { USER_TITLES, getTitleLabel, normalizeTitleForSelect } from '../utils/userRoles';
import { canAccessOzelDosyalar } from '../utils/ozelDosyaAccess';
import './Users.css';
import { onayla } from '../utils/onayla';

const Users = () => {
  const { token, user } = useAuth();
  // Sifre sifirlama yalnizca BARAN kullanicisina acik (sunucu tarafinda da dogrulanir).
  const sifreYetkisi = canAccessOzelDosyalar(user);
  const [sifreHedefi, setSifreHedefi] = useState(null);   // { userId, adSoyad }
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('');
  const [sifreKaydediliyor, setSifreKaydediliyor] = useState(false);
  const [sifreHatasi, setSifreHatasi] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loginHistories, setLoginHistories] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [titleDrafts, setTitleDrafts] = useState({});
  const [savingTitleId, setSavingTitleId] = useState(null);

  useEffect(() => {
    if (token) {
      loadUsers();
    }
  }, [token]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await userAPI.getAll();
      const list = Array.isArray(data) ? data : [];
      setUsers(list);
      const drafts = {};
      list.forEach((u) => {
        const id = u.Id ?? u.id;
        const title = u.Title ?? u.title;
        const role = u.Role ?? u.role;
        if (id != null) drafts[id] = normalizeTitleForSelect(title, role);
      });
      setTitleDrafts(drafts);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Kullanıcılar yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (userData) => {
    setLoading(true);
    try {
      await authAPI.register(userData);
      await loadUsers();
      setShowForm(false);
      alert('Kullanıcı başarıyla oluşturuldu');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!await onayla('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }
    setLoading(true);
    try {
      await userAPI.deleteUser(userId);
      alert('Kullanıcı silindi');
      loadUsers();
      if (expandedUserId === userId) {
        setExpandedUserId(null);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const sifrePenceresiniAc = (userId, adSoyad) => {
    setSifreHedefi({ userId, adSoyad });
    setYeniSifre('');
    setYeniSifreTekrar('');
    setSifreHatasi('');
  };

  const sifrePenceresiniKapat = () => {
    setSifreHedefi(null);
    setYeniSifre('');
    setYeniSifreTekrar('');
    setSifreHatasi('');
  };

  const sifreyiSifirla = async (e) => {
    e.preventDefault();
    if (yeniSifre.length < 8) {
      setSifreHatasi('Şifre en az 8 karakter olmalıdır.');
      return;
    }
    if (yeniSifre !== yeniSifreTekrar) {
      setSifreHatasi('Şifreler birbiriyle uyuşmuyor.');
      return;
    }
    setSifreKaydediliyor(true);
    setSifreHatasi('');
    try {
      await userAPI.resetPassword({ UserId: sifreHedefi.userId, YeniSifre: yeniSifre });
      const ad = sifreHedefi.adSoyad;
      sifrePenceresiniKapat();
      alert(
        ad + 
" kullanıcısının şifresi değiştirildi." +
        String.fromCharCode(10, 10) +
"Açık oturumu kapatıldı; yeni şifresiyle tekrar giriş yapması gerekiyor."
      );
    } catch (error) {
      setSifreHatasi(error.response?.data?.message || error.message || 'Şifre değiştirilemedi.');
    } finally {
      setSifreKaydediliyor(false);
    }
  };

  const handleTitleSave = async (userId) => {
    const title = titleDrafts[userId];
    if (!title) return;
    setSavingTitleId(userId);
    try {
      await userAPI.updateTitle({ UserId: userId, Title: title });
      setUsers((prev) => prev.map((u) => {
        const id = u.Id ?? u.id;
        if (id !== userId) return u;
        return { ...u, Title: title, title };
      }));
      alert('Ünvan güncellendi');
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Ünvan güncellenemedi: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingTitleId(null);
    }
  };

  const handleUserClick = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
    } else {
      setExpandedUserId(userId);
      if (!loginHistories[userId]) {
        setLoadingHistory(true);
        try {
          const history = await userAPI.getUserLoginHistory(userId);
          setLoginHistories(prev => ({
            ...prev,
            [userId]: history
          }));
        } catch (error) {
          console.error('Error loading login history:', error);
          alert('Giriş geçmişi yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
        } finally {
          setLoadingHistory(false);
        }
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatDateForDisplay(dateString);
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Kullanıcı Yönetimi</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          disabled={loading}
        >
          Yeni Kullanıcı Ekle
        </button>
      </div>

      {showForm && (
        <UserForm
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={loading}
        />
      )}

      {loading && !showForm && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>Yükleniyor...</p>
        </div>
      )}

      {!loading && !showForm && (
        <>
          <p style={{ marginBottom: '20px' }}>
            Toplam {users.length} kullanıcı bulundu
          </p>
          <div className="users-list">
            {users.length === 0 ? (
              <div className="empty-state">
                <p>Henüz kullanıcı bulunmamaktadır.</p>
              </div>
            ) : (
              users.map((user, index) => {
                const username = user.Username ?? user.username ?? user.UserName ?? 'N/A';
                const userId = user.Id ?? user.id;
                const userName = user.Name ?? user.name ?? '';
                const userSurname = user.Surname ?? user.surname ?? '';
                const fullName = `${userName} ${userSurname}`.trim() || username;
                const isExpanded = expandedUserId === userId;
                const currentRole = user.Role ?? user.role;
                const currentTitle = user.Title ?? user.title;
                const isSystemAdmin = currentRole === 'Admin' || currentRole === 'admin';
                const draftTitle = titleDrafts[userId] ?? normalizeTitleForSelect(currentTitle, currentRole);

                return (
                  <div key={index} className="user-card">
                    <div className="user-card-main">
                      <div
                        className="user-info clickable"
                        onClick={() => handleUserClick(userId)}
                      >
                        <p><strong>Kullanıcı:</strong> {fullName}</p>
                        <p className="user-meta">
                          <span className="user-role-badge">{getTitleLabel(currentTitle || draftTitle)}</span>
                          {isSystemAdmin && (
                            <span className="user-role-locked" title="Sistem yetkisi — değiştirilmez">Admin</span>
                          )}
                          <span>{isExpanded ? '▼ Giriş geçmişi gösteriliyor' : '▶ Giriş geçmişi için tıklayın'}</span>
                        </p>
                      </div>

                      <div className="user-role-controls" onClick={(e) => e.stopPropagation()}>
                        <select
                          className="form-control user-role-select"
                          value={draftTitle}
                          onChange={(e) => setTitleDrafts((prev) => ({ ...prev, [userId]: e.target.value }))}
                          disabled={savingTitleId === userId}
                          title="Ünvan (izin değildir)"
                        >
                          {USER_TITLES.map((title) => (
                            <option key={title.value} value={title.value}>{title.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleTitleSave(userId)}
                          disabled={savingTitleId === userId || draftTitle === normalizeTitleForSelect(currentTitle, currentRole)}
                        >
                          {savingTitleId === userId ? 'Kaydediliyor...' : 'Ünvan Kaydet'}
                        </button>
                        {sifreYetkisi && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => sifrePenceresiniAc(userId, fullName)}
                            disabled={loading}
                          >
                            Şifre Sıfırla
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(userId)}
                          className="btn btn-danger btn-sm"
                          disabled={loading}
                        >
                          Sil
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="login-history-container">
                        {loadingHistory ? (
                          <p>Yükleniyor...</p>
                        ) : loginHistories[userId] && loginHistories[userId].length > 0 ? (
                          <div className="login-history-list">
                            <h4 style={{ marginBottom: '10px', fontSize: '14px', color: '#333' }}>
                              Giriş Geçmişi ({loginHistories[userId].length} kayıt)
                            </h4>
                            {loginHistories[userId].map((history, idx) => (
                              <div key={idx} className="login-history-item">
                                <span className="login-date">{formatDate(history.LoginDate ?? history.loginDate)}</span>
                                {history.IpAddress && (
                                  <span className="login-ip">IP: {history.IpAddress}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ color: '#666', fontSize: '14px' }}>Henüz giriş kaydı bulunmamaktadır.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {sifreHedefi && (
        <div className="sifre-perde" onClick={sifrePenceresiniKapat}>
          <form
            className="sifre-pencere"
            onClick={(e) => e.stopPropagation()}
            onSubmit={sifreyiSifirla}
          >
            <h3>Şifre Sıfırla</h3>
            <p className="sifre-hedef">{sifreHedefi.adSoyad}</p>

            <label>
              Yeni Şifre
              <input
                type="text"
                className="form-control"
                value={yeniSifre}
                onChange={(e) => setYeniSifre(e.target.value)}
                autoFocus
                autoComplete="off"
                placeholder="En az 8 karakter"
              />
            </label>

            <label>
              Yeni Şifre (Tekrar)
              <input
                type="text"
                className="form-control"
                value={yeniSifreTekrar}
                onChange={(e) => setYeniSifreTekrar(e.target.value)}
                autoComplete="off"
                placeholder="Aynı şifreyi tekrar yazın"
              />
            </label>

            <p className="sifre-not">
              Şifre kaydedildiğinde bu kullanıcının açık oturumu kapanır ve yeni
              şifresiyle tekrar giriş yapması gerekir. Şifreyi kendisine iletmeyi unutmayın.
            </p>

            {sifreHatasi && <p className="sifre-hata">{sifreHatasi}</p>}

            <div className="sifre-dugmeler">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={sifrePenceresiniKapat}
                disabled={sifreKaydediliyor}
              >
                Vazgeç
              </button>
              <button type="submit" className="btn btn-primary" disabled={sifreKaydediliyor}>
                {sifreKaydediliyor ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Users;
